import { Component, type ErrorInfo, type PropsWithChildren } from "react";
import * as Sentry from "@sentry/react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<PropsWithChildren, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("ErrorBoundary caught:", error, info);
    Sentry.captureException(error, {
      extra: { componentStack: info.componentStack },
    });
  }

  render() {
    if (this.state.error) {
      return (
        <div className="mx-auto mt-20 max-w-xl px-4">
          <Card className="border-destructive/50">
            <CardHeader>
              <CardTitle className="text-destructive">Something went wrong</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <pre className="whitespace-pre-wrap text-sm text-muted-foreground">
                {this.state.error.message}
              </pre>
              <Button onClick={() => this.setState({ error: null })}>Reset</Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    return this.props.children;
  }
}
